import ErrorHandler from 'store/utils/ErrorHandler';
import Notify from 'store/utils/Notify';
import {
  getDictionary,
  getPaginationOptions as getDictionariesListPaginationOptions,
} from 'store/services/dictionaries/selectors';
import { getDictionariesList } from 'store/services/dictionaries/actions';
import * as api from './api';
import * as types from './actionTypes';
import {
  getPaginationOptions,
  getDictionaryEntriesList as getDictionaryEntries,
  getSelectedEntries,
} from './selectors';

/**
 * Get Dictionary Entries
 *
 * @param id
 * @param params
 */
export const getDictionaryEntriesList = (id, params = {}) => async (
  dispatch,
  getState,
) => {
  try {
    dispatch({
      type: types.GET_DICTIONARY_ENTRIES_REQUEST,
    });

    const paginationOptions = getPaginationOptions(getState());
    const finalParams = {
      ...paginationOptions,
      ...params,
    };

    const response = await api.getDictionaryEntriesRequest(id, finalParams);

    const payload = {
      dictionaryEntries: response.data.results,
      count: response.data.count,
      paginationOptions: finalParams,
    };

    dispatch({
      type: types.GET_DICTIONARY_ENTRIES_SUCCESS,
      payload,
    });
  } catch (error) {
    dispatch({
      type: types.GET_DICTIONARY_ENTRIES_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

export const sortDictionaryEntriesList = (id, params = {}) => async (
  dispatch,
  getState,
) => {
  try {
    const paginationOptions = getPaginationOptions(getState());

    const finalParams = {
      ...paginationOptions,
      ...params,
      page: 1,
    };

    dispatch({
      type: types.SORT_DICTIONARY_ENTRIES,
      payload: {
        paginationOptions: finalParams,
      },
    });

    const response = await api.getDictionaryEntriesRequest(id, finalParams);

    const payload = {
      dictionaryEntries: response.data.results,
      count: response.data.count,
      paginationOptions: finalParams,
    };

    dispatch({
      type: types.GET_DICTIONARY_ENTRIES_SUCCESS,
      payload,
    });
  } catch (error) {
    dispatch({
      type: types.GET_DICTIONARY_ENTRIES_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Add a new Dictionary Entry
 *
 * @param id
 * @param data
 */
export const addDictionaryEntry = (id, data) => async (dispatch, getState) => {
  try {
    dispatch({
      type: types.ADD_DICTIONARY_ENTRY_REQUEST,
    });

    await api.addDictionaryEntryRequest(id, data);

    dispatch({
      type: types.ADD_DICTIONARY_ENTRY_SUCCESS,
    });

    await dispatch(getDictionaryEntriesList(id, { page: 1 }));

    const { page } = getDictionariesListPaginationOptions(getState());
    await dispatch(getDictionariesList({ page }));

    Notify.success({
      title: 'Dictionary Entry was created successfully',
    });
  } catch (error) {
    dispatch({
      type: types.ADD_DICTIONARY_ENTRY_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'Fail to create Dictionary Entry',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Update Dictionary Entry
 *
 * @param id
 * @param data
 */
export const updateDictionaryEntry = (id, data) => async (
  dispatch,
  getState,
) => {
  try {
    await dispatch({
      type: types.UPDATE_DICTIONARY_ENTRY_REQUEST,
    });

    const dictionaryId = getDictionary(getState()).id;

    await api.updateDictionaryEntryRequest(dictionaryId, id, data);

    dispatch({
      type: types.UPDATE_DICTIONARY_ENTRY_SUCCESS,
    });

    await dispatch(getDictionaryEntriesList(dictionaryId));

    Notify.success({
      title: 'Dictionary Entry was updated successfully',
    });
  } catch (error) {
    dispatch({
      type: types.UPDATE_DICTIONARY_ENTRY_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'Fail to update Dictionary Entry',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Delete Dictionary Entry
 *
 * @param id
 */
export const deleteDictionaryEntry = id => async (dispatch, getState) => {
  try {
    dispatch({
      type: types.DELETE_DICTIONARY_ENTRY_REQUEST,
    });

    const state = getState();
    const dictionaryId = getDictionary(state).id;
    const selected = getSelectedEntries(state);

    await api.deleteDictionaryEntryRequest(dictionaryId, id);

    dispatch({
      type: types.DELETE_DICTIONARY_ENTRY_SUCCESS,
      payload: {
        selected: selected.filter(el => el !== id),
      },
    });

    await dispatch(getDictionaryEntriesList(dictionaryId, { page: 1 }));

    const { page } = getDictionariesListPaginationOptions(getState());
    await dispatch(getDictionariesList({ page }));

    Notify.success({
      title: 'Dictionary Entry was deleted successfully',
    });
  } catch (error) {
    dispatch({
      type: types.DELETE_DICTIONARY_ENTRY_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'Fail to delete Dictionary Entry',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Delete Multiple Dictionary Entries
 */
export const deleteDictionaryEntries = () => async (dispatch, getState) => {
  try {
    dispatch({
      type: types.DELETE_DICTIONARY_ENTRIES_REQUEST,
    });

    const state = getState();
    const dictionaryId = getDictionary(state).id;
    const ids = getSelectedEntries(state).map(id => id);

    await api.deleteDictionaryEntriesRequest(dictionaryId, ids);

    dispatch({
      type: types.DELETE_DICTIONARY_ENTRIES_SUCCESS,
    });

    await dispatch(getDictionaryEntriesList(dictionaryId, { page: 1 }));

    const { page } = getDictionariesListPaginationOptions(getState());
    await dispatch(getDictionariesList({ page }));

    Notify.success({
      title: 'All Dictionary Entries were deleted successfully',
    });
  } catch (error) {
    dispatch({
      type: types.DELETE_DICTIONARY_ENTRIES_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'Fail to update Dictionary Entries',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Toggle entry selection
 *
 * @param selectedId
 * @returns {function(*, *)}
 */
export const toggleEntrySelection = selectedId => (dispatch, getState) => {
  const selected = getSelectedEntries(getState());
  const isSelected = selected.includes(selectedId);

  const selections = isSelected
    ? selected.filter(id => id !== selectedId)
    : [...selected, selectedId];

  dispatch({
    type: types.UPDATE_SELECTED_ENTRIES,
    payload: {
      entries: selections,
    },
  });
};

/**
 * Toggle all entries selections
 *
 * @returns {function(*, *)}
 */
export const toggleAllEntriesSelection = () => (dispatch, getState) => {
  const selected = getSelectedEntries(getState());
  const entries = getDictionaryEntries(getState());

  const selections =
    selected.length === entries.length ? [] : entries.map(({ id }) => id);

  dispatch({
    type: types.UPDATE_SELECTED_ENTRIES,
    payload: {
      entries: selections,
    },
  });
};
